// src/pages/personal/analytics/DashboardSection/DashboardSection.tsx
import React, { useCallback, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Container, Grid } from '@/components/ui/Layout';
import { TEXT_STYLES } from '@/theme';
import DashboardMetrics from './components/DashboardMetrics';
import PerformanceChart from './components/PerformanceChart';
import TimeDistribution from './components/TimeDistribution';
import SalesChannels from './components/SalesChannels/SalesChannels';
import { useDashboardData } from './hooks/useDashboardData';
import { PeriodSelector } from '@/components/ui/PeriodSelector';
import { Period, DateRange, MetricType } from '../types/types';
import styles from './DashboardSection.module.css';
import { useResponsive } from '@/hooks/useResponsive';

// Мемоизированный компонент метрик для предотвращения лишних ререндеров
const MemoizedMetrics = memo(DashboardMetrics);
// Мемоизированный компонент графика
const MemoizedChart = memo(PerformanceChart);

const DashboardSection: React.FC = () => {
  const { t } = useTranslation('dashboard');
  const { isMobile } = useResponsive();
  
  const {
    metrics,
    chartData,
    period,
    selectedMetric,
    peakHours,
    peakDays,
    platformDistribution,
    businessTimezone,
    isLoading,
    error,
    setPeriod,
    setSelectedMetric,
    setDateRange,
    refreshDashboard
  } = useDashboardData();

  // Мемоизируем обработчик изменения периода
  const handlePeriodChange = useCallback((newPeriod: Period, dateRange?: DateRange) => {
    setPeriod(newPeriod);
    
    if (dateRange) {
      // Проверяем валидность dateRange перед обновлением
      if (dateRange.startDate instanceof Date && 
          dateRange.endDate instanceof Date && 
          !isNaN(dateRange.startDate.getTime()) && 
          !isNaN(dateRange.endDate.getTime())) {
        setDateRange(dateRange);
      }
    }
    
    // Если периоды имеют большое различие, обновляем данные
    if ((newPeriod === 'day' && period !== 'day') || 
        (newPeriod !== 'day' && period === 'day') ||
        (newPeriod === 'year' && period !== 'year')) {
      setTimeout(() => {
        refreshDashboard();
      }, 50);
    }
  }, [setPeriod, setDateRange, period, refreshDashboard]);

  // Мемоизируем обработчик изменения метрики
  const handleMetricChange = useCallback((metric: MetricType) => {
    setSelectedMetric(metric);
  }, [setSelectedMetric]);

  return (
    <Container size="full" padding="md">
      <div className="w-full max-w-7xl mx-auto">
        <h1 className={`${TEXT_STYLES.h1} text-center mb-6`}>{t('title')}</h1>
        
        <Grid cols={1} gap="lg">
          {/* Метрики */}
          <MemoizedMetrics 
            metrics={metrics}
            selectedMetric={selectedMetric}
            onSelectMetric={handleMetricChange}
          />
          
          {/* Переключатель периодов - центрирован */}
          <div className="flex justify-center my-4">
            <PeriodSelector 
              period={period} 
              onChange={handlePeriodChange} 
              showTitle={false}
            />
          </div>
          
          {/* График на всю ширину */}
          <MemoizedChart 
            data={chartData}
            metric={selectedMetric}
            period={period}
            isLoading={isLoading}
            hideTitle={true}
          />
          
          {error && (
            <div className="bg-red-100 text-red-700 p-4 rounded-md">
              {t('errors.loading_failed')}
              {process.env.NODE_ENV === 'development' && (
                <div className="mt-2 text-sm">{error}</div>
              )}
            </div>
          )}
          
          {/* Новые компоненты - Распределение времени и каналы */}
          <div className={styles.chartSection}>
            <Grid 
              cols={1} 
              gap={isMobile ? "md" : "lg"}
            >
              {/* TimeDistribution компонент */}
              <TimeDistribution 
                peakHours={peakHours || []}
                peakDays={peakDays || []}
                isLoading={isLoading}
                displayHorizontal={!isMobile} // Вертикальное отображение на мобильных
                businessTimezone={businessTimezone}
              />
              
              {/* SalesChannels компонент */}
              <SalesChannels 
                data={platformDistribution || []} 
                isLoading={isLoading}
              />
            </Grid>
          </div>
        </Grid>
      </div>
    </Container>
  );
};

export default DashboardSection;